import type { TranslateFn } from "@/lib/i18n";
import type {
  DayCloseLateChange,
  DayCloseReportView,
} from "@/lib/api/types";

/**
 * P6.1 (founder, P6-Q1, 2026-10-25) — THE SAVED Z, as the screen, the A4,
 * the CSV and the share read it. One file, so the four cannot word or round
 * a change differently.
 *
 * A closed day's figures are the ones saved when its last drawer closed, and
 * they do not change; `closedDay.changes` lists each figure that moved since,
 * saved and now. The server computes the list from the live Z against the
 * saved one, so `saved + difference = now` for every line.
 */

/** The figures that are counts, not money. */
const COUNT_FIGURES = new Set([
  "sales.invoiceCount",
  "sales.billDiscountCount",
  "sales.refundCount",
  "sales.refundMethodDivergedCount",
  "sales.refundLateCount",
  "openBillCount",
]);

export function isCountFigure(figure: string): boolean {
  return COUNT_FIGURES.has(figure);
}

/**
 * The figure's name on the page: `dayClose.closedDayFigure<Name>`. `sales.x`
 * and `bridge.x` share one key space (no field name is on both). A figure the
 * server added after this build reads as "another figure", never a field name.
 */
export function closedDayFigureLabel(t: TranslateFn, figure: string): string {
  const name = figure.replace(/^(sales|bridge)\./, "");
  const key = `closedDayFigure${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  return t((m) => {
    const labels: Record<string, string> = m.dayClose;
    return Object.prototype.hasOwnProperty.call(labels, key) &&
      key !== "closedDayFigureColumn" &&
      key !== "closedDayFigureOther"
      ? labels[key]
      : m.dayClose.closedDayFigureOther;
  });
}

/** `now − saved`, rounded to the piastre; null when the live Z lacks it. */
export function changeDifference(change: DayCloseLateChange): number | null {
  if (change.now === null) return null;
  return Math.round((change.now - change.saved) * 100) / 100;
}

/**
 * A figure for a change line: a count as a bare integer, money through the
 * caller's formatter, an absent side as «—».
 */
export function changeValue(
  figure: string,
  value: number | null,
  money: (amount: number) => string,
): string {
  if (value === null) return "—";
  return isCountFigure(figure) ? String(value) : money(value);
}

/**
 * The signed difference, for an LTR run (`<bdi dir="ltr">`): a sign on a
 * non-zero difference only (a zero cannot occur on a change line, but would
 * print bare — `report-pages.md`, "A sign is a claim").
 */
export function signedChange(
  change: DayCloseLateChange,
  money: (amount: number) => string,
): string {
  const difference = changeDifference(change);
  if (difference === null) return "—";
  const sign = difference > 0 ? "+" : difference < 0 ? "-" : "";
  const magnitude = Math.abs(difference);
  return `${sign}${isCountFigure(change.figure) ? String(magnitude) : money(magnitude)}`;
}

/**
 * The day's still-open bills NOW, for the Today tile: an operations count, not
 * a figure of the day, so a closed day's tile reads the live count the
 * server sends in `changes` when it moved since the close.
 */
export function liveOpenBillCount(z: DayCloseReportView): number {
  const moved = z.closedDay?.changes.find(
    (change) => change.figure === "openBillCount",
  );
  if (moved && moved.now !== null) return moved.now;
  return z.sales.sequenceExceptions.filter((entry) => entry.kind === "OPEN")
    .length;
}
